import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import PhonelinkLockRoundedIcon from '@mui/icons-material/PhonelinkLockRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

import { accountConsoleUrl } from '@/shared/auth/accountConsole';
import { SettingsCard } from '@/shared/ui';

interface ConsoleLinkRow {
  key: string;
  icon: ReactNode;
  primary: string;
  secondary: string;
  href: string;
}

/**
 * «Пароль и другие способы входа» — three static, no-backend links into the Keycloak Account
 * Console's *Signing in* / *Device activity* pages (user-story-merchant-portal-060 AC9). The portal
 * deliberately lists, renames and removes no credential of its own (user-story-platform-004) —
 * password, the authenticator app and session management stay entirely in the console.
 */
export function AccountConsoleLinksCard() {
  const { t } = useTranslation('account');
  const opensInNewTab = t('security.other.opensInNewTab');

  const rows: ConsoleLinkRow[] = [
    {
      key: 'password',
      icon: <PasswordRoundedIcon fontSize="small" />,
      primary: t('security.other.password'),
      secondary: t('security.other.passwordHint'),
      href: accountConsoleUrl('#/security/signing-in', '/account/security'),
    },
    {
      key: 'totp',
      icon: <PhonelinkLockRoundedIcon fontSize="small" />,
      primary: t('security.other.totp'),
      secondary: t('security.other.totpHint'),
      href: accountConsoleUrl('#/security/signing-in', '/account/security'),
    },
    {
      key: 'sessions',
      icon: <DevicesRoundedIcon fontSize="small" />,
      primary: t('security.other.sessions'),
      secondary: t('security.other.sessionsHint'),
      href: accountConsoleUrl('#/security/device-activity', '/account/security'),
    },
  ];

  return (
    <SettingsCard
      icon={<ShieldRoundedIcon />}
      title={t('security.other.title')}
      description={t('security.other.description')}
      divided
    >
      <List disablePadding>
        {rows.map((row) => (
          <ListItemButton
            key={row.key}
            component="a"
            href={row.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${row.primary} — ${opensInNewTab}`}
            sx={{ px: 0, minHeight: 48 }}
          >
            <ListItemIcon>{row.icon}</ListItemIcon>
            <ListItemText primary={row.primary} secondary={row.secondary} />
            <OpenInNewRoundedIcon fontSize="small" color="action" aria-hidden />
          </ListItemButton>
        ))}
      </List>
    </SettingsCard>
  );
}
